<template>
    <van-popup v-model:show="show" class="reset_pw_popup">
        <div class="header">
            {{ $t('common.isPasswordForget') }}
        </div>
        <div class="content_text">
            {{ $t('common.PWResetInstructions') }}
        </div>
        <div>
            <!-- Step Indicator -->
            <div class="step-container">
                <van-row align="center" justify="space-between" class="step-row">
                    <template v-for="(step, index) in steps" :key="index">
                        <van-col>
                            <div class="step-item" :class="getStepClass(index)">
                                <van-icon v-if="index < currentStepIndex" name="success" />
                                <span v-else>{{ index + 1 }}</span>
                            </div>
                        </van-col>
                        <van-col v-if="index !== steps.length - 1" class="line"
                            :class="{ done: index < currentStepIndex }"></van-col>
                    </template>
                </van-row>
            </div>
            <!-- STEP 1 -->
            <div v-if="stepType === 'identity'">
                <div class="phone_verification_text">
                    {{ $t('common.verifyUserID') }}
                </div>
                <InputField v-model="phoneNumberInReset" @valueChanged="phoneChange" :labelText="$t('profile.phoneNum')"
                    :isshowFlag="true" fieldType="number" :isShowError="phoneValidationNotice" />

                <div v-if="phoneValidationNotice" class="errorNotice">
                    <van-icon name="warning-o" />
                    {{ $t('loginRegister.phoneFormat') }}
                </div>

                <CaptchaField :captcha-img="codeData?.img ? 'data:image/png;base64,' + codeData.img : ''"
                    @valueChanged="captcha = $event" @updateCaptcha="getCaptcha" />

                <MainButton :title="$t('common.resetPasswordNow')" radius="5px !important"  :activeStatus="isFormValid" 
                @click="verifyUser" btn-padding="30px !important" style="margin: 2rem 0 1rem;" :isBtnLoading="btnLoading" />
            </div>

            <!-- STEP 2: PHONE -->
            <SendOtpPopup v-if="stepType === 'phone'" :verificationData="verificationData" :phoneNumber="phoneNumberInReset"
                :uuid="codeData?.uuid" @verified="onOtpVerified" :emailVerified="isEmailVerified"
                :phoneVerified="isPhoneVerified" :stepType="stepType" />

            <!-- STEP 3: EMAIL -->
            <SendOtpPopup v-if="stepType === 'email'" :verificationData="verificationData" :phoneNumber="phoneNumberInReset"
                :uuid="codeData?.uuid" @verified="onOtpVerified" :emailVerified="isEmailVerified"
                :phoneVerified="isPhoneVerified" :stepType="stepType" />

            <!-- STEP 4: RESET -->
            <ResetPasswordStep v-if="stepType === 'reset'" :uuid="codeData?.uuid" :fpUuid="fpUuid" :contact="phoneNumberInReset"
                :contactType="contactType" :areaCode="'55'" @reset="onPasswordReset" />
        </div>
    </van-popup>
    <StatusPopup ref="statusRef" :is-show-success="isSuccess" :content="toastContent" />
</template>
<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import InputField from './resetPassword/inputField.vue';
import { getCaptchaCode, fpCheckUserExist } from '@/api/user';
import CaptchaField from '../components/captchaField.vue';
import SendOtpPopup from './resetPassword/sendOtpPopup.vue';
import ResetPasswordStep from './resetPassword/resetPasswordStep.vue';
import { useRouter } from 'vue-router';
import MainButton from '../../components/common/mainButton.vue';
import { RegisterLoginData } from '@/view/loginRegister/supportScript';
import StatusPopup from '@/components/common/statusPopup.vue';
const emits = defineEmits(['valueChanged', 'close', 'captchaChage']);
const props = defineProps<{
    dataSet: RegisterLoginData
}>()
const router = useRouter();
const contactType = ref('')
const show = ref(false);
const codeData = ref<any>(null);
const verificationData = ref<any>(null);
const isEmailVerified = ref(false);
const isPhoneVerified = ref(false);
const phoneValidationNotice = ref(false);
const stepsReady = ref(false);
const btnLoading = ref(false);
const phoneNumberInReset = ref('');
const captcha = ref('');
const isSuccess = ref(false)
const toastContent = ref('')
const statusRef = ref()
const fpUuid = ref('')
const stepType = ref<'identity' | 'phone' | 'email' | 'reset'>('identity');
const steps = computed(() => {
    if (!stepsReady.value) {
        return ['identity'];
    }
    const arr = ['identity'];
    if (isPhoneVerified.value) {
        arr.push('phone');
    }
    if (isEmailVerified.value) {
        arr.push('email');
    }
    arr.push('reset');
    return arr;
});

const currentStepIndex = computed(() => {
    return steps.value.findIndex(s => s === stepType.value);
});

const getStepClass = (index: number) => {
    if (index < currentStepIndex.value) return 'done';
    if (index === currentStepIndex.value) return 'active';
    return '';
};

const goToNextStep = () => {
    const index = currentStepIndex.value;
    if (index < steps.value.length - 1) {
        stepType.value = steps.value[index + 1] as any;
    }
};

const open = () => {
    close();
    show.value = true;
    stepType.value = 'identity';
    getCaptcha();
};

const close = () => {
    emits('close')
}

// Get captcha
const getCaptcha = () => {
    getCaptchaCode().then(res => {
        codeData.value = res.data;
    });
};

// Verify user
const verifyUser = async () => {
    const params = {
        contact: phoneNumberInReset.value,
        uuid: codeData.value.uuid,
        captcha: captcha.value.trim(),
        forgetPasswordCaptcha: codeData.value.forgetPasswordCaptchaEnabled,
        fpCaptchaType: "CAPTCHA-FP-step-1"
    };
    try {
        btnLoading.value = true;
        const res = await fpCheckUserExist(params);
        btnLoading.value = false;
        if (res.code === 200) {
            showSuccess('');
            verificationData.value = res.data;
            isPhoneVerified.value = res.data?.mobileVerified || false;
            isEmailVerified.value = res.data?.emailVerified || false;
            fpUuid.value = res.data?.fpUuid || '';
            if(isPhoneVerified.value){
                if(res.data?.smsVerified){
                    contactType.value = 'SMS';
                } else if(res.data?.whatsappVerified){
                    contactType.value = 'WHATSAPP';
                }
            } else if(isEmailVerified.value){
                contactType.value = 'EMAIL';
            }
            if (!isPhoneVerified.value && !isEmailVerified.value) {
                showError(res.msg);
                getCaptcha();
            } else {
                showSuccess('');
                stepsReady.value = true;
                setTimeout(() => {
                    goToNextStep();
                }, 500);
                await nextTick();
            }
        } else {
            getCaptcha();
            showError(res.msg)
        }
    } catch (err) {
        getCaptcha();
        btnLoading.value = false;
        const message = err instanceof Error ? err.message : String(err);
        showError(message);
    }
};

const onOtpVerified = async () => {
    await nextTick();
    goToNextStep();
};

const onPasswordReset = () => {
    show.value = false;
    router.push('/home');
};

const checkPhoneValidation = (value: string) => {
  if ((value.length === 11 && value[2] === '9') || value.length === 0) {
    phoneValidationNotice.value = false
  } else {
    phoneValidationNotice.value = true
  }
}

const phoneChange = (value: string) => {
  phoneNumberInReset.value = value
  checkPhoneValidation(value)
}

const isCaptchaValid = computed(() => {
  return captcha.value.trim().length > 0
})

const isFormValid = computed(() => {
  return (
    phoneNumberInReset.value.length === 11 &&
    phoneNumberInReset.value[2] === '9' &&
    !phoneValidationNotice.value &&
    isCaptchaValid.value
  )
})

const showError = (message: string) => {
    toastContent.value = message;
    isSuccess.value = false;
    statusRef.value.open()
}

const showSuccess = (message: string) => {
    toastContent.value = message;
    isSuccess.value = true;
    statusRef.value.open()
}

defineExpose({ open });
</script>
<style scoped>
.reset_pw_popup {
    width: 90%;
    height: auto;
    /* min-height: 70%; */
    z-index: 10002;
    background: #101629;
    border-radius: 15px;
    padding: 1rem;
}

.header {
    color: #fff;
    font-size: 21px;
}

.content_text {
    color: #ffffff66;
    font-size: 15px;
}

.content_text p {
    margin: 4px 0;
}

.phone_verification_text {
    color: #ffffff66;
    font-size: 15px;
}

.step-container {
    padding: 20px 15px;
}

.step-row {
    align-items: center;
}

.step-item {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
}

.step-item.active {
    background-color: #35A5F8;
    border: none;
    color: #fff;
    width: 30px;
    height: 30px;
}

.step-item.done {
    background-color: #35A5F8;
    border: none;
    color: #fff;
    width: 24px;
    height: 24px;
}

.line {
    flex: 1;
    height: 2px;
    background-color: rgba(255, 255, 255, 0.3);
}

.line.done {
    background-color: #35A5F8;
}

.van-icon {
    font-size: 16px;
}

.errorNotice {
    color: #E00000;
    font-size: 12px;
    padding: 0 10px;
    font-weight: 500;
}
</style>